import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Bars3Icon, 
  XMarkIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const { currentUser, userProfile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsProfileOpen(true);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing to allow mouse movement
    const timeout = setTimeout(() => {
      setIsProfileOpen(false);
    }, 150); // 150ms delay
    setHoverTimeout(timeout);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      // Disable body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable body scroll
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Meet the Team', path: '/team' },
    { name: 'Programmes', path: '/programmes' },
    { name: 'Contact', path: '/contact' },
  ];

  const userNavItems = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: 'Competitions', path: '/competitions' },
    { name: 'Merchandise', path: '/merchandise' },
    { name: 'Profile', path: '/profile' },
  ];

  const adminNavItems = [
    { name: 'Admin Dashboard', path: '/admin' },
    { name: 'Manage Users', path: '/admin/users' },
    { name: 'Manage Products', path: '/admin/products' },
    { name: 'Manage Orders', path: '/admin/orders' },
    { name: 'Manage Articles', path: '/admin/articles' },
    { name: 'Manage Programs', path: '/admin/programs' },
    { name: 'Manage Competitions', path: '/admin/competitions' },
  ];

  return (
    <nav className="bg-gray-900/95 backdrop-blur-sm border-b border-gray-700/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img src="/oldhamIcon.jpg" alt="Oldham Athletics" className="w-8 h-8 mr-3" />
              <span className="text-xl font-bold text-white">Oldham Athletics</span>
            </Link>
          </div>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-oldham-gold bg-oldham-blue/20'
                    : 'text-gray-300 hover:text-oldham-gold hover:bg-gray-800/50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* User menu */}
          <div className="hidden md:flex items-center space-x-4">
            {currentUser ? (
              <div 
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-oldham-gold"
                >
                  <UserCircleIcon className="w-8 h-8 text-gray-300" />
                  <span className="ml-2 text-gray-300">{userProfile?.first_name || 'User'}</span>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-700">
                    {/* User menu items */}
                    {userNavItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-150"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                    
                    {/* Admin menu items */}
                    {isAdmin() && (
                      <>
                        <div className="border-t border-gray-600 my-1"></div>
                        {adminNavItems.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white transition-colors duration-150"
                            onClick={() => setIsProfileOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </>
                    )}
                    
                    <div className="border-t border-gray-600 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors duration-150"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-gray-300 hover:text-oldham-gold px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  Sign In
                </Link>
                <div className="h-6 w-px bg-gray-600"></div>
                <Link
                  to="/register"
                  className="bg-oldham-gold text-gray-900 hover:bg-oldham-gold/90 font-medium py-2 px-4 rounded-lg transition-all duration-200 text-sm"
                >
                  Join Us
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-oldham-gold hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-oldham-gold"
            >
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden absolute left-0 right-0 top-full bg-gray-800 border-t border-gray-700 max-h-[calc(100vh-4rem)] overflow-y-auto z-40 shadow-lg">
          <div className="px-2 pt-2 pb-20 space-y-1 sm:px-3">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(item.path)
                    ? 'text-oldham-gold bg-oldham-blue/20'
                    : 'text-gray-300 hover:text-oldham-gold hover:bg-gray-700'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            
            {currentUser && (
              <>
                <div className="border-t border-gray-600 my-1"></div>
                {userNavItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-oldham-gold hover:bg-gray-700"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                
                {isAdmin() && (
                  <>
                    <div className="border-t border-gray-600 my-1"></div>
                    {adminNavItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.path}
                        className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-oldham-gold hover:bg-gray-700"
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </>
                )}
                
                <div className="border-t border-gray-600 my-1"></div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-primary-400 hover:bg-gray-700"
                >
                  Sign out
                </button>
              </>
            )}
            
            {!currentUser && (
              <>
                <div className="border-t border-gray-600 my-1"></div>
                <div className="px-3 py-2 space-y-2">
                  <Link
                    to="/login"
                    className="block w-full text-center px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-oldham-gold hover:bg-gray-700 transition-colors duration-200"
                    onClick={() => setIsOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="block w-full text-center px-4 py-2 rounded-lg text-sm font-medium text-gray-900 bg-oldham-gold hover:bg-oldham-gold/90 transition-colors duration-200"
                    onClick={() => setIsOpen(false)}
                  >
                    Join Us
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
